import { ArrowLeft, Compass, Home, Settings, X } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { fetchMyCommunities, type LibraryCommunity, timeAgo } from '../../lib/communities';
import { fetchMyGames, type LibraryGame } from '../../lib/games';
import { useSession } from '../../lib/session';
import { ChatDock, MessagesLink, useCanChat } from '../chat/ChatDock';
import { Avatar, initials } from '../community/Avatar';
import { GameIcon } from '../games/GameArt';
import { Logo } from '../Logo';
import { SiteFooter } from '../SiteFooter';
import { buttonClass } from '../ui';
import { BellLink, SearchLink, TopBar } from './TopBar';

const FEED = { to: '/', label: 'Feed', icon: Home };
const DISCOVER = { to: '/discover', label: 'Discover', icon: Compass };

const KIND_NAMES: Record<string, string> = { test: 'Private test', team: 'Private team' };

/** "Private team · 2 waiting". */
export function communityLine(community: LibraryCommunity) {
  const kind = KIND_NAMES[community.kind] ?? 'Community';
  return community.requestCount ? `${kind} · ${community.requestCount} waiting` : kind;
}

interface LibraryData {
  games: LibraryGame[] | null;
  communities: LibraryCommunity[] | null;
}

/** Your games and the communities you're in, for the library; null while loading. */
function useLibrary(signedIn: boolean): LibraryData {
  const [library, setLibrary] = useState<LibraryData>({ games: null, communities: null });
  useEffect(() => {
    if (!signedIn) return;
    let active = true;
    fetchMyCommunities()
      .then((result) => active && setLibrary((l) => ({ ...l, communities: result.communities })))
      .catch(() => active && setLibrary((l) => ({ ...l, communities: [] })));
    fetchMyGames()
      .then((result) => active && setLibrary((l) => ({ ...l, games: result.games })))
      .catch(() => active && setLibrary((l) => ({ ...l, games: [] })));
    return () => {
      active = false;
    };
  }, [signedIn]);
  return library;
}

/** "Played 2 hours ago", or that you joined its community. */
const gameLine = (game: LibraryGame) =>
  game.lastPlayedAt ? `Played ${timeAgo(game.lastPlayedAt)}` : 'You joined its community';

const rowClass = (current: boolean) =>
  `flex min-h-[52px] items-center gap-3 rounded-xl px-2 py-1.5 ${current ? 'bg-field' : 'hover:bg-field'}`;

/**
 * Your library: the games you play (M29) and the communities you're in. It opens on Games
 * when there are any.
 */
function Library({ games, communities, here }: LibraryData & { here: string }) {
  const [chosen, setChosen] = useState<'games' | 'communities' | null>(null);
  const tab = chosen ?? (games?.length || !communities?.length ? 'games' : 'communities');
  const isHere = (path: string) => here === path || here.startsWith(`${path}/`);
  return (
    <>
      <fieldset className="m-0 grid min-w-0 grid-cols-2 rounded-full border-0 bg-field p-1">
        <legend className="sr-only">Library</legend>
        {(['games', 'communities'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={tab === value}
            onClick={() => setChosen(value)}
            className={`flex h-9 items-center justify-center rounded-full text-sm font-semibold ${tab === value ? 'bg-card text-ink shadow-[0_1px_2px_rgba(27,21,47,0.1)]' : 'text-muted hover:text-ink'}`}
          >
            {value === 'games' ? 'Games' : 'Communities'}
          </button>
        ))}
      </fieldset>
      <div className="mt-2">
        {tab === 'games' && games === null && (
          <p role="status" className="px-2 py-4 text-sm text-muted">
            Loading…
          </p>
        )}
        {tab === 'games' && games?.length === 0 && (
          <p className="px-2 py-4 text-sm leading-relaxed text-muted">
            Games you play or join show here.
          </p>
        )}
        {tab === 'games' && games && games.length > 0 && (
          <ul>
            {games.map((game) => (
              <li key={game.id}>
                <Link
                  to={game.path}
                  aria-current={isHere(game.path) ? 'page' : undefined}
                  className={rowClass(isHere(game.path))}
                >
                  <GameIcon game={game} className="size-10 rounded-[11px] text-sm" />
                  <span className="min-w-0 flex-1 leading-[18px]">
                    <b className="block truncate text-sm font-semibold">{game.name}</b>
                    <span className="text-[13px] text-muted">{gameLine(game)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {tab === 'communities' && communities === null && (
          <p role="status" className="px-2 py-4 text-sm text-muted">
            Loading…
          </p>
        )}
        {tab === 'communities' && communities?.length === 0 && (
          <p className="px-2 py-4 text-sm leading-relaxed text-muted">
            Communities you join show here. Open an invite link to join one.
          </p>
        )}
        {tab === 'communities' && communities && communities.length > 0 && (
          <ul>
            {communities.map((community) => {
              const current = isHere(community.path);
              return (
                <li key={community.id}>
                  <Link
                    to={community.path}
                    aria-current={current ? 'page' : undefined}
                    className={rowClass(current)}
                  >
                    <span
                      aria-hidden="true"
                      className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-brand font-display text-sm font-bold text-ink"
                    >
                      {initials(community.name)}
                    </span>
                    <span className="min-w-0 flex-1 leading-[18px]">
                      <b className="block truncate text-sm font-semibold">{community.name}</b>
                      <span className="text-[13px] text-muted">{communityLine(community)}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

/** Your username and account, at the bottom of the library. */
function Profile({ username }: { username: string }) {
  return (
    <div className="flex items-center gap-3 border-t border-line pt-3.5">
      <Avatar name={username} />
      <Link to="/account" className="min-w-0 flex-1 leading-[18px]">
        <b className="block truncate font-semibold">{username}</b>
        <span className="text-[13px] text-muted">Your account</span>
      </Link>
      <Link
        to="/account"
        aria-label="Settings"
        className="flex size-10 items-center justify-center rounded-full bg-field text-ink"
      >
        <Settings size={20} strokeWidth={1.9} aria-hidden="true" />
      </Link>
    </div>
  );
}

/**
 * mogzo.com's frame, phone first (M28). On a phone: a header with your picture (it opens your
 * library), the logo, search and the bell, and Feed · Discover along the bottom. On a computer
 * (M33): a header across the top with the logo, search, the bell and your account; the
 * library down the left edge; and the page filling the rest (banners run to the right edge;
 * each page centres its own content). `title` gives a page a phone header with a back button
 * instead; `header` replaces the phone header outright, and `search` the computer header's
 * search box (the search page's own). Nothing in this frame is indexed by search engines yet:
 * private communities never are, and the public side waits for the launch.
 */
export function AppFrame({
  title,
  header,
  headerEnd,
  search,
  fullHeight = false,
  children,
}: {
  title?: string;
  /** The page fills the screen below the computer header and draws its own phone header (Messages). */
  fullHeight?: boolean;
  header?: ReactNode;
  /** In the computer header's middle instead of its search box. */
  search?: ReactNode;
  /** At the end of the phone header (the feed's recent games, once scrolled past). */
  headerEnd?: ReactNode;
  children: ReactNode;
}) {
  const { me, publicSide } = useSession();
  const canChat = useCanChat();
  const { pathname, key } = useLocation();
  // Discover opens with the public side: staff now, everyone at the launch (M29). Until then
  // the phone's bottom bar would hold only Feed, so it isn't shown.
  const TABS = publicSide ? [FEED, DISCOVER] : [FEED];
  const bottomBar = TABS.length > 1 && !fullHeight;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const library = useLibrary(Boolean(me));
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLButtonElement>(null);

  // The drawer closes once a link in it is followed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every navigation
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    closeButton.current?.focus();
    const button = opener.current;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      button?.focus();
    };
  }, [open]);

  const back = () => (key === 'default' ? navigate('/') : navigate(-1));
  const tabCurrent = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));

  const libraryButton = me ? (
    <button
      ref={opener}
      type="button"
      aria-label="Open your library and settings"
      aria-expanded={open}
      onClick={() => setOpen(true)}
      className="flex size-11 items-center justify-center rounded-full"
    >
      <Avatar name={me.username} size="sm" />
    </button>
  ) : null;

  return (
    <div className="min-h-dvh bg-canvas">
      <meta name="robots" content="noindex" />
      <TopBar search={search} />

      <div className="lg:flex">
        {/* Computer: the library down the left. */}
        <aside
          aria-label="MOGZO"
          className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-[280px] shrink-0 flex-col border-r border-line bg-card pt-4 pr-3.5 pb-[18px] pl-[22px] lg:flex"
        >
          <nav aria-label="Main" className="flex flex-col gap-1">
            {TABS.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                aria-current={tabCurrent(to) ? 'page' : undefined}
                className={`flex h-12 items-center gap-3.5 rounded-full px-3.5 font-semibold ${tabCurrent(to) ? 'bg-brand text-ink' : 'hover:bg-field'}`}
              >
                <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
                {label}
              </Link>
            ))}
          </nav>
          {me ? (
            <div className="mt-5 min-h-0 flex-1 overflow-y-auto border-t border-line pt-4">
              <h2 className="mb-2.5 ml-1.5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
                Library
              </h2>
              <Library {...library} here={pathname} />
            </div>
          ) : null}
        </aside>

        <div className="flex min-h-dvh min-w-0 flex-1 flex-col lg:min-h-[calc(100dvh-4rem)]">
          {/* Phone: the header. */}
          <header
            className={`sticky top-0 z-20 h-14 items-center gap-2.5 border-b border-line bg-card px-2.5 lg:hidden ${fullHeight ? 'hidden' : 'flex'}`}
          >
            {header ??
              (title ? (
                <>
                  <button
                    type="button"
                    aria-label="Back"
                    onClick={back}
                    className="flex size-10 items-center justify-center rounded-full"
                  >
                    <ArrowLeft size={22} strokeWidth={1.9} aria-hidden="true" />
                  </button>
                  <span className="min-w-0 flex-1 truncate font-semibold">{title}</span>
                  {libraryButton}
                </>
              ) : (
                <>
                  {libraryButton}
                  <Logo size="sm" />
                  <div className="ml-auto flex min-w-0 items-center gap-1">
                    {headerEnd}
                    <SearchLink />
                    {canChat && <MessagesLink />}
                    {me && <BellLink />}
                    {me === null && (
                      <Link to="/sign-in" className={buttonClass('primary', false)}>
                        Sign in
                      </Link>
                    )}
                  </div>
                </>
              ))}
          </header>
          <div className="flex-1">{children}</div>
          {!fullHeight && (
            <div className={bottomBar ? 'pb-[78px] lg:pb-0' : ''}>
              <SiteFooter />
            </div>
          )}
        </div>
      </div>

      {/* Computer: the chat window (M38), kept open from page to page. */}
      {canChat && <ChatDock />}

      {/* Phone: Feed · Discover along the bottom. */}
      {bottomBar && (
        <nav
          aria-label="Main"
          className="fixed inset-x-0 bottom-0 z-30 grid h-[70px] grid-cols-2 border-t border-line bg-card px-2 pt-2 pb-3 lg:hidden"
        >
          {TABS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              aria-current={tabCurrent(to) ? 'page' : undefined}
              className={`flex flex-col items-center gap-1 text-xs font-semibold ${tabCurrent(to) ? 'text-ink' : 'text-muted'}`}
            >
              <span
                className={`flex h-[30px] w-14 items-center justify-center rounded-full ${tabCurrent(to) ? 'bg-brand' : ''}`}
              >
                <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
              </span>
              {label}
            </Link>
          ))}
        </nav>
      )}

      {/* Phone: the library, from the picture in the header. */}
      {open && me && (
        <div className="lg:hidden">
          <button
            type="button"
            aria-label="Close the library"
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-ink/45"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-labelledby="library-title"
            className="fixed inset-y-0 left-0 z-50 flex w-[326px] max-w-[85vw] flex-col bg-card shadow-[0_12px_40px_rgba(27,21,47,0.25)]"
          >
            <div className="flex items-center justify-between pt-3.5 pr-3 pb-1.5 pl-5">
              <h2 id="library-title" className="text-[22px] leading-7 font-bold tracking-[-0.01em]">
                Library
              </h2>
              <button
                ref={closeButton}
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="flex size-10 items-center justify-center rounded-full"
              >
                <X size={22} strokeWidth={1.9} aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pt-2 pb-2">
              <Library {...library} here={pathname} />
            </div>
            <div className="px-3 pb-[22px] pl-5">
              <Profile username={me.username} />
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
